import { useEffect, useRef, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { Button } from "@pps/ui/components/ui/button";
import { obterAtualizadorPwa, type AtualizadorPwa, type EstadoPwa } from "@/lib/pwa-update";

const SEM_ATUALIZADOR: EstadoPwa = { recarregarPendente: false, prontoOffline: false };
const assinarNada = () => () => undefined;

/**
 * Estado da atualização do PWA (lib/pwa-update.ts) + recarga na troca de rota.
 * Precisa estar DENTRO do <BrowserRouter> e do <QueryClientProvider>.
 * Sem SW (Capacitor nativo, dev) devolve sempre "nada pendente".
 */
export function usePwaUpdate(atualizador: AtualizadorPwa | null = obterAtualizadorPwa()) {
  const estado = useSyncExternalStore(
    atualizador?.subscribe ?? assinarNada,
    atualizador?.getSnapshot ?? (() => SEM_ATUALIZADOR),
  );

  // Mutation em andamento bloqueia a recarga automática (troca de rota e aba oculta):
  // recarregar no meio de um save perderia a escrita ou a deixaria sem resposta na tela.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!atualizador) return;
    atualizador.definirEscritaPendente(() => queryClient.isMutating() > 0);
    return () => atualizador.definirEscritaPendente(null);
  }, [atualizador, queryClient]);

  // Troca de rota com versão nova pendente: a tela anterior já foi desmontada pelo router,
  // então recarregar aqui não perde trabalho e já abre a rota nova na versão nova.
  const { pathname } = useLocation();
  const rotaAnterior = useRef(pathname);
  useEffect(() => {
    if (rotaAnterior.current === pathname) return;
    rotaAnterior.current = pathname;
    atualizador?.aoTrocarDeRota();
  }, [pathname, atualizador]);

  return {
    recarregarPendente: estado.recarregarPendente,
    atualizarAgora: () => atualizador?.atualizarAgora(),
  };
}

/**
 * Aviso persistente "Nova versão disponível — Atualizar".
 * Faixa própria em vez de toast: o toaster do CRM mostra UM toast por vez (TOAST_LIMIT = 1),
 * então qualquer "Salvo!" posterior apagaria o aviso.
 */
export function PwaUpdateBanner({ atualizador }: { atualizador?: AtualizadorPwa | null }) {
  const { t } = useTranslation();
  const { recarregarPendente, atualizarAgora } = usePwaUpdate(
    atualizador === undefined ? obterAtualizadorPwa() : atualizador,
  );
  if (!recarregarPendente) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[100] mx-auto flex max-w-md items-center gap-3 rounded-lg border bg-background p-3 shadow-lg sm:left-auto sm:right-4 sm:mx-0"
    >
      <RefreshCw className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{t("pwaUpdate.title")}</p>
        <p className="text-xs text-muted-foreground">{t("pwaUpdate.description")}</p>
      </div>
      <Button size="sm" onClick={atualizarAgora}>
        {t("pwaUpdate.action")}
      </Button>
    </div>
  );
}
